import { FunctionComponent, useMemo } from 'react';

import { ListType } from 'constants/listTypes';
import { InternalOnChangeProp } from 'modules/workflowNodeEditors/editorComponents/ResourceSelect/ResourceSelect.types';

import { gql } from '@apollo/client';
import {
  FetchListParams,
  getListSelectFetchItemsVariables,
  ListSelect,
  OnChangeHandler,
} from 'modules/workflowNodeEditors/components/formElements';
import { SORT_DIRECTIONS } from 'constants/sortDirections';
import moment from 'moment';
import styled from 'styled-components';
import { SelectWrapper } from 'ui/selects/Selects.styles';

const StyledSelectWrapper = styled(SelectWrapper)`
  border: none;
`;

export type FilterSegmentResourceSelectProps = {
  onChange: (args: InternalOnChangeProp) => void;
  id: string;
  placeholder: string;
  isDisabled?: boolean;
  isFilterable?: boolean;
  onSearchChange?: (filterText: string | undefined) => void;
  hasError?: boolean;
  listType: ListType[];
  value: string;
  Trigger?: FunctionComponent<React.PropsWithChildren<any>>;
};

export const fetchListsQuery = gql`
  query getLists($filterInfo: ListFilterInfoInput, $search: String, $sort: Sort) {
    lists(
      filterInfo: $filterInfo
      pagination: { limit: 25, offset: 0 }
      search: $search
      sort: $sort
    ) {
      results {
        id
        name
        listType
        creatorUserId
        updatedAt
      }
    }
  }
`;
export const getDaysAgo = (date: string) => {
  // strip out times to properly calculate day difference
  const currentDate = moment().startOf('day');
  const pastDate = moment(date).startOf('day');

  return currentDate.diff(pastDate, 'days');
};
export const parseListOption = ({
  listType,
  id,
  name,
  updatedAt,
}: {
  listType: string;
  id: string;
  name: string;
  updatedAt: string;
}) => ({
  id: `${name}-${id}`,
  label: name,
  doubleLine: `List ID: ${id} | ${listType} | Updated ${getDaysAgo(updatedAt)} days ago`,
  value: `${id}`,
});

export const getResourceListVariables = (params?: FetchListParams) => ({
  ...getListSelectFetchItemsVariables(params),
  sort: {
    sortBy: 'UpdatedAt',
    sortDirection: SORT_DIRECTIONS.DESCENDING,
  },
});

export const FilterSegmentResourceSelect = ({
  onChange,
  listType,
  ...props
}: FilterSegmentResourceSelectProps) => {
  const variables = useMemo(() => getResourceListVariables({ listType }), [listType]);
  const handleChange: OnChangeHandler = (_val, { option }) => {
    // Fixes WF-2231
    // TODO: Look further into why this is being triggered in the first place
    if (option) {
      onChange({
        ...option,
        label: `${option.label}`,
      });
    }
  };

  return (
    <ListSelect
      data-formfieldtype="select"
      data-selectoptiontype="list"
      {...props}
      isMulti={false}
      listType={listType}
      fetchAllItemsQuery={fetchListsQuery}
      fetchAllItemsVariables={variables}
      parseOption={parseListOption}
      onChange={handleChange}
      SelectWrapper={StyledSelectWrapper}
    />
  );
};
